import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { UserLabelsTable } from "./-components/user-labels-table";
import { DashboardLayout } from "@/components/dashboard-layout";
import { useQuery } from "@tanstack/react-query";
import { organisationsQueryOptions } from "@/server/organisations";
import { useContext, useMemo, useEffect } from "react";
import { DashboardContext } from "@/components/dashboard-layout";
import { useSearch } from "@tanstack/react-router";
import { UserLabelSidebar } from "./-components/user-label-sidebar";
import { userLabelsQueryOptions } from "@/server/user-labels";
import { useRightSidebarClose } from "@/hooks/use-right-sidebar-close";
import { useRightSidebarOpen } from "@/hooks/use-right-sidebar-open";
import { toast } from "sonner";
import { CrudEntry } from "@/lib/mma/types.gen";
import {
  checkIfUserMayRead,
  checkIfUserMayWrite,
  checkIfUserMayUpdate,
  checkIfUserMayDelete,
  ContextUser,
  getNextRedirectRoute,
} from "@/lib/utils";

export const Route = createFileRoute("/_dashboard/user-labels")({
  component: () => (
    <DashboardLayout submenu={null}>
      <UserLabelsRouteComponent />
    </DashboardLayout>
  ),
  beforeLoad: ({ context }) => {
    const user = context.user as ContextUser;
    const mayRead = checkIfUserMayRead(user?.profile, user?.accessControl, "USER_LABEL");

    if (!mayRead) {
      const nextRedirectRoute = getNextRedirectRoute(user);
      throw redirect({ to: nextRedirectRoute });
    }
  },
  loader: async ({ context }) => {
    return {
      crumb: "User labels",
      userProfileId: (context as { user?: { profile: string } }).user?.profile,
      accessControl: (context as { user?: { accessControl: Record<string, CrudEntry> } }).user?.accessControl,
    };
  },
});

function UserLabelsRouteComponent() {
  const navigate = useNavigate();
  const userLabelsQuery = useQuery(userLabelsQueryOptions());
  const userLabels = userLabelsQuery.data;
  const loadingUserLabels = userLabelsQuery.isLoading;
  const organisationsQuery = useQuery(organisationsQueryOptions());
  const organisations = organisationsQuery.data;
  const searchParams = useSearch({ strict: false }) as {
    key?: string;
  };
  const { userProfileId, accessControl } = Route.useLoaderData();
  //If the user has no profile, it means they have access to everything. Profiles are restrictive only.
  const mayWrite = checkIfUserMayWrite(userProfileId, accessControl, "USER_LABEL");
  const mayUpdate = checkIfUserMayUpdate(userProfileId, accessControl, "USER_LABEL");
  const mayDelete = checkIfUserMayDelete(userProfileId, accessControl, "USER_LABEL");
  const modifiedUserLabels = useMemo(
    () =>
      userLabels?.map((label: any) => ({
        ...label,
        organisations: label?.organisations?.map((organisation: any) => {
          const foundOrg = organisations?.find((o: any) => o.id.toString() === organisation);
          return foundOrg
            ? { value: foundOrg.id.toString(), label: foundOrg.name }
            : { value: label.key, label: label.key };
        }),
      })),
    [userLabels, organisations]
  );

  const context = useContext(DashboardContext);
  const closeSidebar = useRightSidebarClose();
  const openSidebar = useRightSidebarOpen();

  // Build sidebar component based on search params
  const sidebarComponent = useMemo(() => {
    if (!searchParams.key) return null;

    if (searchParams.key === "new") {
      return <UserLabelSidebar labelKey={null} mayWrite={mayWrite} />;
    }

    if (modifiedUserLabels) {
      const label = modifiedUserLabels.find((label: any) => label.key?.toString() === searchParams.key);
      if (label) {
        return <UserLabelSidebar labelKey={label.key} mayUpdate={mayUpdate} mayDelete={mayDelete} />;
      }
    }

    return null;
  }, [searchParams.key, modifiedUserLabels, mayWrite, mayUpdate, mayDelete]);

  if (!context) {
    console.error("DashboardContext is null. Ensure DashboardLayout wraps this component.");
    return <div>Error: DashboardContext is not available.</div>;
  }

  // Handle sidebar opening/closing based on search params
  useEffect(() => {
    if (!context) {
      return;
    }

    const hasPermission = searchParams.key !== "new" || mayWrite;

    openSidebar({
      component: sidebarComponent,
      paramKeys: "key",
      shouldClose: false,
      permissionCheck: !hasPermission,
      onPermissionDenied: () => closeSidebar("key"),
    });
  }, [sidebarComponent, mayWrite, searchParams.key, openSidebar, closeSidebar]);

  const handleAddLabel = () => {
    navigate({
      to: "/user-labels",
      replace: true,
      search: (prev) => ({ ...prev, key: "new" }),
    });
  };

  useEffect(() => {
    if (userLabelsQuery.isError) {
      const message =
        userLabelsQuery.error instanceof Error
          ? userLabelsQuery.error.message
          : "Failed to load user labels. Please try again later.";
      toast.error(message);
    }
  }, [userLabelsQuery.isError, userLabelsQuery.error]);

  return (
    <UserLabelsTable
      labels={modifiedUserLabels}
      handleAddLabel={handleAddLabel}
      loadingUserLabels={loadingUserLabels}
      closeSidebar={closeSidebar}
      mayWrite={mayWrite}
      mayDelete={mayDelete}
    />
  );
}
